import axios from 'axios';
import { storage } from '@shared/lib/storage';
import {
  Collection, Folder, Note, UserInfo,
  Organization, OrgMember,
  NoteCollaborator, ShareLink, Comment,
  CollaboratorPermission, OrgMemberRole,
  Task, TaskAssignee, TaskLink, TaskLinkType,
} from '@shared/types';

// API Configuration
const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:9099'
  : 'https://api.hintword.com';

const WS_BASE = process.env.NODE_ENV === 'development'
  ? 'ws://localhost:9099'
  : 'wss://api.hintword.com';

const axiosInstance = axios.create({
  baseURL: API_BASE,
});

axiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      storage.removeItem('hintword_token');
      // Browser only; RN reacts to the cleared token via auth state.
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Types
interface GoogleAuthResponse {
  data: {
    token: string;
  };
  status: number;
}

interface ApiResponse<T> {
  data: T;
  status: number;
  error?: string;
}

// WebSocket utility for note create/update
function sendNoteSync(payload: any): Promise<Note> {
  return new Promise((resolve, reject) => {
    const token = storage.getItem('hintword_token');
    if (!token) {
      reject(new Error('No authentication token found'));
      return;
    }

    // Pass token as query param
    const ws = new WebSocket(`${WS_BASE}/ws/note-sync?token=${encodeURIComponent(token)}`);

    ws.onopen = () => {
      ws.send(JSON.stringify({ payload }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.note) {
          ws.close();
          resolve(data.note);
        } else {
          reject(new Error('Invalid response from note-sync'));
        }
      } catch (err) {
        reject(err);
      }
    };

    ws.onerror = (err) => {
      reject(err);
    };
  });
}

// Notes REST API for create/update
async function createOrUpdateNote(noteData: Partial<Note>): Promise<Note> {
  const token = storage.getItem('hintword_token');
  if (!token) throw new Error('No authentication token found');
  const response = await axiosInstance.post('/v1/note/create', noteData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const data = response.data;
  if (data.status === -1) {
    throw new Error(data.error || 'Failed to create/update note');
  }
  return data.data;
}

// Grammar check API
async function checkGrammar({ html, noteId }: { html: string, noteId: string }) {
  const token = storage.getItem('hintword_token');
  if (!token) throw new Error('No authentication token found');
  const response = await axiosInstance.post('/v1/agent/completion', {
    context: 'grammar',
    note: html,
    note_id: noteId,
  }, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const data = response.data;
  if (data?.data?.message) {
    return data.data.message; // corrected HTML
  }
  throw new Error('Grammar correction failed');
}

// API Functions
export const api = {
  // Auth
  googleAuth: async (userPayload: UserInfo): Promise<GoogleAuthResponse> => {

    const response = await fetch(`${API_BASE}/v1/auth/google/user-info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userPayload),
    });

    if (!response.ok) {
      throw new Error('Failed to authenticate with backend');
    }

    return response.json();
  },

  // Collections
  getCollections: async (): Promise<ApiResponse<Collection[]>> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get('/v1/tab/collection', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = response.data;
    if (data.status === -1) {
      throw new Error(data.error || 'Failed to fetch collections');
    }
    return { ...data, data: data.collection };
  },

  createCollection: async (collectionData: { name: string }): Promise<ApiResponse<Collection>> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post('/v1/tab/collection', {
      ...collectionData,
      status: 'ACTIVE',
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = response.data;
    if (data.status === -1) {
      throw new Error(data.error || 'Failed to create collection');
    }
    return { ...data, data: Array.isArray(data.collection) ? data.collection[0] : data.collection };
  },

  reorderCollections: async (collections: Array<{ collection_id: string; sequence: number }>): Promise<ApiResponse<Collection[]>> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post('/v1/tab/reorder', {
      collections,
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = response.data;
    if (data.status === -1) {
      throw new Error(data.error || 'Failed to reorder collections');
    }
    return { ...data, data: data.collection };
  },

  reorderTabs: async (collectionId: string, tabs: Array<{ tab_id: string; sequence: number }>): Promise<ApiResponse<any[]>> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post('/v1/tab/reorder-tabs', {
      collection_id: collectionId,
      tabs,
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = response.data;
    if (data.status === -1) {
      throw new Error(data.error || 'Failed to reorder tabs');
    }
    return { ...data, data: data.tabs };
  },

  createOrUpdateTab: async (tabData: any): Promise<ApiResponse<any>> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post('/v1/tab/create', tabData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = response.data;
    if (data.status === -1) {
      throw new Error(data.error || 'Failed to create or update tab');
    }
    return data;
  },

  getNotes: async (): Promise<any> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get('/v1/note/list', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = response.data;
    if (data.status === -1) {
      throw new Error(data.error || 'Failed to fetch notes');
    }
    return data.notes;
  },

  // Note sync via WebSocket
  sendNoteSync,
  // Note REST create/update
  createOrUpdateNote,
  checkGrammar,

  // Notes shared with me. `permissions` maps note_id → the caller's best
  // permission (VIEW/COMMENT/EDIT) so the UI can render read-only vs editable.
  getSharedNotes: async (): Promise<{ notes: Note[]; permissions: Record<string, CollaboratorPermission> }> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get('/v1/note/shared', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to fetch shared notes');
    return { notes: data.notes ?? [], permissions: data.permissions ?? {} };
  },

  // ─── Collaborators ─────────────────────────────────────────────────────────

  getCollaborators: async (noteId: string): Promise<NoteCollaborator[]> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get(`/v1/note/${noteId}/collaborators`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.data.data;
  },

  addCollaborator: async (noteId: string, payload: { email?: string; org_id?: string; permission: CollaboratorPermission }): Promise<NoteCollaborator> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post(`/v1/note/${noteId}/collaborators`, payload, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to add collaborator');
    return data.data;
  },

  updateCollaborator: async (noteId: string, collaboratorId: string, permission: CollaboratorPermission): Promise<NoteCollaborator> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.put(`/v1/note/${noteId}/collaborators/${collaboratorId}`, { permission }, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    return response.data.data;
  },

  removeCollaborator: async (noteId: string, collaboratorId: string): Promise<void> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    await axiosInstance.delete(`/v1/note/${noteId}/collaborators/${collaboratorId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
  },

  // ─── Share links ───────────────────────────────────────────────────────────

  getShareLinks: async (noteId: string): Promise<ShareLink[]> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get(`/v1/note/${noteId}/links`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.data.data;
  },

  createShareLink: async (noteId: string, payload: { permission: CollaboratorPermission; expires_at?: string }): Promise<ShareLink> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post(`/v1/note/${noteId}/links`, payload, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to create share link');
    return data.data;
  },

  revokeShareLink: async (noteId: string, linkId: string): Promise<void> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    await axiosInstance.delete(`/v1/note/${noteId}/links/${linkId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
  },

  // ─── Comments ──────────────────────────────────────────────────────────────

  getComments: async (noteId: string): Promise<Comment[]> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get(`/v1/note/${noteId}/comments`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.data.data;
  },

  addComment: async (noteId: string, content: string, parentCommentId?: string): Promise<Comment> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post(`/v1/note/${noteId}/comments`,
      { content, parent_comment_id: parentCommentId ?? null },
      { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } },
    );
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to add comment');
    return data.data;
  },

  updateComment: async (noteId: string, commentId: string, content: string): Promise<Comment> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.put(`/v1/note/${noteId}/comments/${commentId}`,
      { content },
      { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } },
    );
    return response.data.data;
  },

  deleteComment: async (noteId: string, commentId: string): Promise<void> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    await axiosInstance.delete(`/v1/note/${noteId}/comments/${commentId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
  },

  // ─── Organizations ─────────────────────────────────────────────────────────

  getOrgs: async (): Promise<OrgMember[]> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get('/v1/org', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.data.data;
  },

  createOrg: async (name: string, avatarUrl?: string): Promise<Organization> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post('/v1/org',
      { name, avatar_url: avatarUrl },
      { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } },
    );
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to create organization');
    return data.data;
  },

  getOrgMembers: async (orgId: string): Promise<OrgMember[]> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get(`/v1/org/${orgId}/members`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.data.data;
  },

  inviteMember: async (orgId: string, email: string, role: OrgMemberRole): Promise<OrgMember> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post(`/v1/org/${orgId}/members`,
      { email, role },
      { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } },
    );
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to invite member');
    return data.data;
  },

  removeMember: async (orgId: string, memberId: string): Promise<void> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    await axiosInstance.delete(`/v1/org/${orgId}/members/${memberId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
  },

  updateMemberRole: async (orgId: string, memberId: string, role: OrgMemberRole): Promise<OrgMember> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.put(`/v1/org/${orgId}/members/${memberId}`,
      { role },
      { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } },
    );
    return response.data.data;
  },

  // ─── Folders ───────────────────────────────────────────────────────────────

  getFolders: async (): Promise<Folder[]> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get('/v1/folder', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.data.data ?? [];
  },

  createFolder: async (payload: { name: string; description?: string }): Promise<Folder> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post('/v1/folder', payload, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to create folder');
    return data.data;
  },

  updateFolder: async (slug: string, payload: { name?: string; description?: string }): Promise<Folder> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.put(`/v1/folder/${slug}`, payload, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to update folder');
    return data.data;
  },

  deleteFolder: async (slug: string): Promise<void> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    await axiosInstance.delete(`/v1/folder/${slug}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
  },

  // ─── Tasks ─────────────────────────────────────────────────────────────────

  getTasks: async (includeArchived = false): Promise<Task[]> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get('/v1/task', {
      headers: { 'Authorization': `Bearer ${token}` },
      params: { include_archived: includeArchived },
    });
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to fetch tasks');
    return data.data ?? [];
  },

  createTask: async (payload: {
    title: string;
    description?: object | null;
    org_id?: string;
    note_id?: string;
    status?: string;
    due_date?: string;
  }): Promise<Task> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post('/v1/task', payload, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to create task');
    return data.data;
  },

  getTask: async (taskId: string): Promise<Task> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get(`/v1/task/${taskId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Task not found');
    return data.data;
  },

  updateTask: async (taskId: string, payload: {
    title?: string;
    description?: object | null;
    status?: string;
    note_id?: string;    // "" to clear, "<id>" to set, omit to leave unchanged
    is_archived?: boolean;
    due_date?: string;   // "" to clear, "YYYY-MM-DD" to set, omit to leave unchanged
    sequence?: number;
  }): Promise<Task> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.put(`/v1/task/${taskId}`, payload, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to update task');
    return data.data;
  },

  deleteTask: async (taskId: string): Promise<void> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    await axiosInstance.delete(`/v1/task/${taskId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
  },

  addTaskAssignee: async (taskId: string, userId: string): Promise<TaskAssignee> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post(`/v1/task/${taskId}/assignee`, { user_id: userId }, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to add assignee');
    return data.data;
  },

  removeTaskAssignee: async (taskId: string, userId: string): Promise<void> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    await axiosInstance.delete(`/v1/task/${taskId}/assignee/${userId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
  },

  addTaskLink: async (taskId: string, linkedTaskId: string, linkType: TaskLinkType = 'RELATED'): Promise<TaskLink> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post(`/v1/task/${taskId}/link`, { linked_task_id: linkedTaskId, link_type: linkType }, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = response.data;
    if (data.status === -1) throw new Error(data.error || 'Failed to add task link');
    return data.data;
  },

  removeTaskLink: async (taskId: string, linkId: string): Promise<void> => {
    const token = storage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    await axiosInstance.delete(`/v1/task/${taskId}/link/${linkId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
  },
};